(function () {
  'use strict';

  function safeStorageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeStorageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      document.cookie = encodeURIComponent(key) + '=' + encodeURIComponent(value) + '; path=/';
    }
  }

  function safeStorageRemove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      document.cookie = encodeURIComponent(key) + '=; Max-Age=0; path=/';
    }
  }

  function parseJSON(value) {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function hasExpiredSuppression(record) {
    if (!record || typeof record !== 'object' || !record.expiresAt) return false;
    return Date.now() > Number(record.expiresAt);
  }

  function createPopupController(root) {
    var dialog = root.querySelector('[data-promo-dialog]');
    var overlay = root.querySelector('[data-promo-overlay]');
    var closeButton = root.querySelector('[data-promo-close]');
    var declineButton = root.querySelector('[data-promo-decline]');
    var form = root.querySelector('[data-promo-form]');
    var formWrap = root.querySelector('[data-promo-form-wrap]');
    var success = root.querySelector('[data-promo-success]');

    if (!dialog || !overlay || !closeButton || !form) return;

    var config = {
      enabled: root.dataset.enabled === 'true',
      storageKey: root.dataset.storageKey || 'pop-model',
      suppressionDays: Number(root.dataset.suppressionDays || 30),
      pageviewThreshold: Math.max(2, Number(root.dataset.pageviewThreshold || 2)),
      delayMs: Number(root.dataset.delaySeconds || 0) * 1000,
      enablePageview: root.dataset.pageviewTrigger === 'true',
      enableExitIntent: root.dataset.exitTrigger === 'true',
      showOnceSession: root.dataset.showOnceSession !== 'false',
      enableEditorPreview: root.dataset.editorPreview !== 'false',
      debug: root.dataset.debugLogs === 'true'
    };

    if (!config.enabled) return;

    var sessionShownKey = config.storageKey + '__shown_session';
    var pageViewKey = config.storageKey + '__page_views';
    var triggerLocked = false;
    var isOpen = false;
    var lastFocusedElement = null;
    var mobileScrollPeak = window.scrollY;
    var inactivityTimer = null;
    var hasMobileFallbackTriggered = false;
    var isThemeEditor = Boolean(window.Shopify && window.Shopify.designMode);

    function log() {
      if (!config.debug) return;
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[PromoPopup]');
      console.log.apply(console, args);
    }

    function getSuppressionRecord() {
      return parseJSON(safeStorageGet(config.storageKey));
    }

    function isSuppressed() {
      var record = getSuppressionRecord();
      if (!record) return false;

      if (hasExpiredSuppression(record)) {
        safeStorageRemove(config.storageKey);
        return false;
      }

      return true;
    }

    function setSuppressed() {
      var expiresAt = Date.now() + config.suppressionDays * 24 * 60 * 60 * 1000;
      safeStorageSet(config.storageKey, JSON.stringify({ expiresAt: expiresAt }));
    }

    function hasShownThisSession() {
      if (!config.showOnceSession) return false;
      return safeStorageGet(sessionShownKey) === '1';
    }

    function markShownThisSession() {
      safeStorageSet(sessionShownKey, '1');
    }

    function eligibleToShow() {
      if (isSuppressed()) return false;
      if (hasShownThisSession()) return false;
      return true;
    }

    function lockBodyScroll() {
      document.documentElement.classList.add('promo-popup-modal-body-lock');
      document.body.classList.add('promo-popup-modal-body-lock');
    }

    function unlockBodyScroll() {
      document.documentElement.classList.remove('promo-popup-modal-body-lock');
      document.body.classList.remove('promo-popup-modal-body-lock');
    }

    function getFocusableElements() {
      return dialog.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])'
      );
    }

    function trapFocus(event) {
      if (!isOpen || event.key !== 'Tab') return;

      var focusable = getFocusableElements();
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function closePopup(reason) {
      if (!isOpen) return;
      isOpen = false;
      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      unlockBodyScroll();

      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus();
      }

      // Close, decline and successful submit all suppress future displays.
      if (reason === 'close' || reason === 'decline' || reason === 'submit') {
        setSuppressed();
        log('suppression set from action:', reason);
      }
    }

    function openPopup(triggerSource) {
      if (isOpen || triggerLocked || !eligibleToShow()) return;
      triggerLocked = true;
      isOpen = true;
      lastFocusedElement = document.activeElement;
      markShownThisSession();
      root.classList.add('is-open');
      root.setAttribute('aria-hidden', 'false');
      lockBodyScroll();

      window.requestAnimationFrame(function () {
        closeButton.focus();
      });

      root.setAttribute('data-trigger-source', triggerSource || 'unknown');
      log('opened from trigger:', triggerSource || 'unknown');
    }

    function scheduleOpen(source, options) {
      var useDelay = !options || options.useDelay !== false;
      var delay = useDelay ? Math.max(0, config.delayMs) : 0;
      window.setTimeout(function () {
        openPopup(source);
      }, delay);
    }

    function trackPageViewTrigger() {
      if (!config.enablePageview || !eligibleToShow()) return;
      var count = Number(safeStorageGet(pageViewKey) || 0) + 1;
      safeStorageSet(pageViewKey, String(count));

      if (count >= config.pageviewThreshold) {
        scheduleOpen('page-view', { useDelay: true });
      }
      log('page-view count:', count);
    }

    function setupDesktopExitIntent() {
      document.addEventListener(
        'mouseout',
        function (event) {
          if (!config.enableExitIntent || !eligibleToShow() || window.innerWidth < 990) return;
          if (event.clientY <= 8 && !event.relatedTarget) {
            scheduleOpen('exit-intent-desktop', { useDelay: false });
            log('desktop exit-intent detected');
          }
        },
        { passive: true }
      );
    }

    function setupMobileFallbackIntent() {
      if (!config.enableExitIntent || window.innerWidth >= 990) return;

      function triggerFromMobileFallback() {
        if (hasMobileFallbackTriggered || !eligibleToShow()) return;
        hasMobileFallbackTriggered = true;
        scheduleOpen('exit-intent-mobile', { useDelay: false });
        log('mobile fallback intent detected');
      }

      // Lightweight inactivity fallback for touch devices.
      function resetInactivityTimer() {
        window.clearTimeout(inactivityTimer);
        inactivityTimer = window.setTimeout(function () {
          triggerFromMobileFallback();
        }, 14000);
      }

      resetInactivityTimer();
      document.addEventListener('touchstart', resetInactivityTimer, { passive: true });
      document.addEventListener('scroll', resetInactivityTimer, { passive: true });

      // Scroll-up fallback once user has scrolled down significantly.
      window.addEventListener(
        'scroll',
        function () {
          var currentY = window.scrollY;
          if (currentY > mobileScrollPeak) {
            mobileScrollPeak = currentY;
            return;
          }

          if (mobileScrollPeak > 420 && mobileScrollPeak - currentY > 120) {
            triggerFromMobileFallback();
          }
        },
        { passive: true }
      );
    }

    root.addEventListener('click', function (event) {
      if (event.target === overlay || event.target.closest('[data-promo-close]')) {
        closePopup('close');
      }
    });

    if (declineButton) {
      declineButton.addEventListener('click', function () {
        closePopup('decline');
      });
    }

    document.addEventListener('keydown', function (event) {
      if (!isOpen) return;
      if (event.key === 'Escape') {
        closePopup('close');
        return;
      }
      trapFocus(event);
    });

    form.addEventListener('submit', function () {
      // Keep this as a simple bridge: works with Shopify newsletter form now,
      // and can be swapped to Klaviyo/AJAX later without changing section markup.
      if (formWrap && success) {
        formWrap.classList.add('is-hidden');
        success.classList.remove('is-hidden');
      }
      window.setTimeout(function () {
        closePopup('submit');
      }, 900);
    });

    // If Shopify posts back with success, keep state consistent and suppress.
    if (success && !success.classList.contains('is-hidden')) {
      setSuppressed();
    }

    // Theme editor preview mode: open automatically so merchants can style and test quickly.
    if (isThemeEditor && config.enableEditorPreview) {
      scheduleOpen('theme-editor-preview', { useDelay: true });
      log('theme editor preview trigger scheduled');
      return;
    }

    // If page-view trigger is disabled, fall back to delay-only behavior.
    if (!config.enablePageview && eligibleToShow()) {
      scheduleOpen('delay-only', { useDelay: true });
      log('delay-only trigger scheduled (page-view disabled)');
    } else {
      trackPageViewTrigger();
    }

    setupDesktopExitIntent();
    setupMobileFallbackIntent();
    log('initialized', {
      enabled: config.enabled,
      storageKey: config.storageKey,
      suppressionActive: isSuppressed(),
      shownThisSession: hasShownThisSession(),
      isThemeEditor: isThemeEditor
    });
  }

  function initPromoPopups() {
    var sections = document.querySelectorAll('.promo-popup-modal-section');
    if (!sections.length) return;
    sections.forEach(createPopupController);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPromoPopups);
  } else {
    initPromoPopups();
  }
})();
